import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { createStore, applyMiddleware } from 'redux';
import { composeWithDevTools } from '@redux-devtools/extension';
import thunk from 'redux-thunk';
import combinedReducers from './store/reducers';
import { Provider } from 'react-redux';
import { CookiesProvider } from 'react-cookie';
import { ThemeProvider, StylesProvider, jssPreset } from '@mui/styles';
import { ThemeProvider as MuiV5ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import rtl from 'jss-rtl';
import { create } from 'jss';
import { SnackbarProvider } from 'notistack';
import ScrollToTop from './components/ScrollToTop';
import { createAppTheme, createAppV5Theme } from './theme';
import { bootstrapMigration } from './platform/storage';
import { applySafeAreaCssVars } from './platform/safeArea';
// spec 000231 — app-wide picker localization (Hebrew, 24-hour).
import AppPickerLocalization from './components/primitives/AppPickerLocalization';

// import main style file
import '../src/styles/main.css';

// Expose safe-area + bar-height CSS vars (e.g. var(--safe-area-top),
// var(--app-bar-height)) for layout code. Idempotent. No-op on web.
applySafeAreaCssVars();

// Best-effort: copy any legacy localStorage keys into Capacitor Preferences
// before render. Idempotent. Native only — no-op on web.
bootstrapMigration();

const rtlTheme = createAppTheme('rtl');
const v5RtlTheme = createAppV5Theme('rtl');
const jss = create({ plugins: [...jssPreset().plugins, rtl()] });
const cacheRtl = createCache({ key: 'muirtl', stylisPlugins: [rtlPlugin] });

const initialState = {};

const middleware = [thunk];
const devTools =
  process.env.NODE_ENV === 'production'
    ? applyMiddleware(...middleware)
    : composeWithDevTools(applyMiddleware(...middleware));

const store = createStore(combinedReducers, initialState, devTools);

const root = createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
        <CookiesProvider>
            <Provider store={store}>
                <BrowserRouter>
                    <ScrollToTop />
                    <CacheProvider value={cacheRtl}>
                        <MuiV5ThemeProvider theme={v5RtlTheme}>
                            <StylesProvider jss={jss}>
                                <ThemeProvider theme={rtlTheme}>
                                    <SnackbarProvider
                                        maxSnack={1}
                                        autoHideDuration={3000}
                                        style={{
                                            position: 'fixed',
                                            height: 60,
                                            width: '80vw',
                                            bottom: '7vh',
                                            right: '7vw',
                                            zIndex: '2000 !important',
                                        }}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'center',
                                        }}>
                                        <AppPickerLocalization>
                                            <App />
                                        </AppPickerLocalization>
                                    </SnackbarProvider>
                                </ThemeProvider>
                            </StylesProvider>
                        </MuiV5ThemeProvider>
                    </CacheProvider>
                </BrowserRouter>
            </Provider>
        </CookiesProvider>
    </React.StrictMode>
);

// register();
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals(console.log);
reportWebVitals();
